import { action, computed, flow, flowResult, makeObservable, observable } from 'mobx'
import { DeliverySlot, OrderAddress, ReservedSlotResponse, ReservePreBookSlotResponse } from '../types/store/basket'
import { Nullable } from '../types/utils'
import { RootStore } from './RootStore'
import { StoreModel } from './utils'
import { isBefore } from 'date-fns'
import { DateUtils } from '../utils/date-utils'
import { CommerceAPI } from 'commerce-api'
import { Status, StatusMap } from '../types/commerce'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { BasketStore } from './BasketStore'
import { CustomerAddress } from './CustomerStore'
import { ShopperCustomers } from 'commerce-api'

export class PreBookStore implements StoreModel {
  api: CommerceAPI
  status: Status
  rootStore: RootStore
  basketStore: BasketStore
  preBookDeliverySlots: Nullable<DeliverySlot>
  preBookAddress: OrderAddress | CustomerAddress | null | undefined 
  preBookDeliveryPostalCode: string | undefined
  preBookExpiry: string | undefined
  currentPreBookDeliverySlot?: ReservedSlotResponse
  currentPreBookDeliverySlotId?: string
  isAppliedPostCodeDeliverable: Nullable<boolean>
  prebookedOrder: ShopperCustomers.Order | null

  constructor(rootStore: RootStore, initialState?: ReturnType<RootStore['getState']>['preBookStore']) {
    this.status = StatusMap.INIT
    this.basketStore = new BasketStore(rootStore, initialState?.basketStore)
    this.preBookDeliverySlots = null
    this.preBookAddress = null
    this.preBookDeliveryPostalCode = ''
    this.preBookExpiry = undefined
    this.currentPreBookDeliverySlot = undefined
    this.currentPreBookDeliverySlotId = ''
    this.isAppliedPostCodeDeliverable = null
    this.prebookedOrder = null

    makeObservable(this, {
      isAppliedPostCodeDeliverable: observable,
      preBookAddress: observable,
      preBookDeliveryPostalCode: observable,
      preBookExpiry: observable,
      currentPreBookDeliverySlot: observable,
      currentPreBookDeliverySlotId: observable,
      setCurrentPreBookDeliverySlotId: observable,
      preBookDeliverySlots: observable,
      status: observable,
      prebookedOrder: observable,
      hasValidPreBookedSlot: computed,
      preBookAddressDisplay: computed,
      getPreBookDeliverySlots: flow.bound,
      getPrebookedOrder: flow.bound,
      setPreBookPostalCode: flow.bound,
      reservePreBookSlot: flow.bound,
      setPreBookOrder: action.bound,
      setPreBookAddress: action.bound,
      setPreBookExpiry: action.bound, 
      setCurrentPreBookSlot: action.bound
    })
    this.api = rootStore.api
    this.rootStore = rootStore
  }

  get asJson() {
    return {
      status: this.status,
      preBookDeliverySlots: this.preBookDeliverySlots,
      basketStore: this.basketStore.asJson,
      preBookDays: this.preBookDays,
      preBookSeasonalName: this.preBookSeasonalName,
    }
  }

  get preBookDeliverySlotArray(): DeliverySlot[] {
    if (this.preBookDeliverySlots) {
      return Object.values(this.preBookDeliverySlots)
    } else {
      return []
    }
  }

  get preBookAddressDisplay() {
    const {address1, address2, city, postalCode} = this.preBookAddress || {}
    return [address1, address2, city, postalCode].filter(Boolean).join(', ')
  }

  get preBookDeliveryAddress(): OrderAddress | CustomerAddress | null | undefined {
    return this.preBookAddress
  }

  get preBookEnabled () {
    return Boolean(this.rootStore.globalStore.customSitePreferences?.preBookEnabled)
  }

  get isPreBookForNewCustomersEnabled () {
    return Boolean(this.rootStore.globalStore.customSitePreferences?.preBookForNewCustomersEnabled)
  }

  get preBookDays() {
    return this.rootStore.globalStore.customSitePreferences?.preBookDays || 0 as number
  }

  get preBookSeasonalName() {
    return this.rootStore.globalStore.customSitePreferences?.preBookSeasonalName || '' as string
  }

  setPreBookOrder(value: ShopperCustomers.Order | null) {
    this.prebookedOrder = value
  }
  
  setPreBookAddress(value: OrderAddress | CustomerAddress | null | undefined) {
    this.preBookAddress = value
  }
  
  setPreBookExpiry(expiry: string | undefined) {
    this.preBookExpiry = expiry
  }
  
  setCurrentPreBookSlot(value?: ReservedSlotResponse) {
    this.currentPreBookDeliverySlot = value
  }
  
  setCurrentPreBookDeliverySlotId(slotId: string | undefined) {
    this.currentPreBookDeliverySlotId = slotId
  }

  resetPreBook() {
    this.setPreBookOrder(null)
    this.setCurrentPreBookDeliverySlotId(undefined)
    this.setCurrentPreBookSlot(undefined)
    this.setPreBookPostalCode(undefined)
    this.setPreBookAddress(null)
  }
  
  *getPrebookedOrder(){
    const orderNo = this.rootStore.customerStore.customerInfo?.c_preBookOrderID
    if (!orderNo) {
      this.resetPreBook()
      return
    }

    const res: ShopperCustomers.Order = yield this.rootStore.orderStore.getOrder(orderNo)
    if (res) {
      this.setPreBookOrder(res)
    } else {
      throw new Error('Error fetching pre-booked order')
    }
  }
  
  *setPreBookPostalCode(postcode: string | undefined) {
    this.preBookDeliveryPostalCode = postcode
    if (this.preBookDeliveryPostalCode) {
      yield this.setIsAppliedPostCodeDeliverable(this.preBookDeliveryPostalCode)
    }
    return this.preBookDeliveryPostalCode
  }

  async setIsAppliedPostCodeDeliverable(postcode: string) {
    const postcodeDeliveryStatus = await flowResult(this.basketStore.handlePostcodeDeliveryStatus(postcode))
    this.isAppliedPostCodeDeliverable = postcodeDeliveryStatus
    return postcodeDeliveryStatus
  }

  *getPreBookDeliverySlots() {
    if (!this.preBookDeliveryPostalCode) return

    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetPreBookDeliverySlotsJSON?postcode=${
          this.preBookDeliveryPostalCode
        }`,
        {credentials: 'include'},
      )
      const json: {slots?: DeliverySlot; error?: string} = yield res.json()
      if (json?.slots) {
        this.status = 'init'
        this.preBookDeliverySlots = json.slots
      }

      if (json?.error) {
        this.status = 'error'
        throw new Error('Error fetching pre-book delivery slots')
      }
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  /**
   * Checks if the prebooked order associated with the customers account is valid
   * @returns boolean result
   */
  get hasValidPreBookedSlot() {
    const currentDateTime = new Date().getTime()
    const expiryDateTime =  new Date(this.prebookedOrder?.c_slotExpiryTime).getTime()
    const isPreBookedSlotExpired = currentDateTime < expiryDateTime

    return !!this.prebookedOrder?.c_deliverySlotId && isPreBookedSlotExpired
  }

  /**
   * Checks if a prebook slot reservation has expired or not
   * @returns boolean result 
   */
  isPrebookReservationExpired() {
    if (this.preBookExpiry) {
      if (!DateUtils.validateDate(new Date(this.preBookExpiry))) {
        return false
      }

      // Check if expiry date time is before the current date time
      return isBefore(DateUtils.utcToZonedDate(this.preBookExpiry), DateUtils.current())
    }
    return false
  }

  /**
   * Soft-reserves a pre-book slot prior to checkout
   * @param slotId the slot ID to prebook
   * @returns result of prebook request
   */
  *reservePreBookSlot(slotId: string) {
      try {
          const formData = new FormData()
          formData.append('postcode', '' + this.preBookDeliveryPostalCode)
          formData.append('addressID', '' + (this.preBookAddress?.c_customerAddressID || this.preBookAddress?.addressId))
          formData.append('slotId', slotId)
          const res: Response = yield fetch(
              `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-ReservePreBookSlot?slotId=${slotId}`,
              {
                  method: 'POST',
                  credentials: 'include',
                  body: formData,
              }
          )

          if (!res.ok) {
              const result: ReservePreBookSlotResponse = {
                  result: { code: '400' }
              }
              return result
          }

          const json: ReservePreBookSlotResponse = yield res.json()
          const code = json?.result?.code

          if (code && code !== 'success') {
              // Handle the non-success code
              const result: ReservePreBookSlotResponse = {
                  result: { code }
              }
              return result
          }

          // Updates the prebook data on success
          if (code && code === 'success') {
              this.setCurrentPreBookDeliverySlotId(json.result?.slotReservationId)
              this.setPreBookExpiry(json.result?.slotExpiryTime)
              this.setCurrentPreBookSlot(json.result)
          }

          return json
      } catch (error) {
          const result: ReservePreBookSlotResponse = {
              result: { code: '300' }
          }
          console.error(error)
          return result
      }
    }
}
